import React, { useState, useEffect, useRef } from 'react';
import { shippingService } from '../../services/shippingService';

const InPostPointSelector = ({
  selectedPoint,
  onSelect,
  onClear,
  label = 'Wybierz paczkomat',
  placeholder = 'Wpisz miasto, kod (00-000) lub nazwę paczkomatu (np. WAW123M)...'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  // Zamknij dropdown przy kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Wyszukaj paczkomaty z debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchPoints(searchTerm);
      }, 300);
    } else {
      setPoints([]);
      setShowDropdown(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      // Anuluj trwające zapytanie przy zmianie frazy / odmontowaniu
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [searchTerm]);

  const searchPoints = async (query) => {
    // Anuluj poprzednie zapytanie i oznacz sekwencję - liczy się tylko najnowsze
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current && !signal.aborted;

    try {
      setLoading(true);
      setError(null);

      const trimmed = query.trim();
      const upperQuery = trimmed.toUpperCase();

      // Sprawdź czy to kod pocztowy (format XX-XXX lub XXXXX)
      const isPostCode = /^\d{2}-?\d{3}$/.test(trimmed);

      // Sprawdź czy wygląda jak nazwa paczkomatu (np. WAW123M, KRA01A, POZ10N)
      // Format: 2-4 litery + 1-4 cyfry + opcjonalne litery
      const isLockerName = /^[A-Za-z]{2,4}\d{1,4}[A-Za-z]{0,2}$/i.test(trimmed);

      let allPoints = [];

      // Regex prefiksów ulic (ul., al., os., pl., ...) na początku zapytania.
      // WYMAGANY separator po prefiksie (kropka lub spacja) — inaczej regex
      // obcinalby poczatek nazw miast bez separatora, np. "Plock"->"ock",
      // "Ostrow"->"trow", "Aleksandrow"->"eksandrow".
      const streetPrefix = /^(ul|ulica|al|aleja|os|osiedle|pl|plac)(\.\s*|\s+)/i;

      // 1. Szukaj po kodzie pocztowym
      if (isPostCode) {
        const response = await shippingService.getPoints({
          post_code: trimmed.replace('-', ''),
          per_page: 30,
          signal
        });
        if (response.success && response.data?.items) {
          allPoints = response.data.items;
        }
      }
      // 2. Szukaj po nazwie paczkomatu
      else if (isLockerName) {
        const response = await shippingService.getPoints({
          name: upperQuery,
          per_page: 30,
          signal
        });
        if (response.success && response.data?.items) {
          allPoints = response.data.items;
        }
      }
      // 3. Szukaj po mieście (pierwsze słowo jeśli jest kilka)
      else {
        // Usuń prefiks ulicy z początku zapytania - inaczej trafia do zapytania
        // o miasto i psuje wynik (pusta lista). Fallback do oryginału gdyby
        // po usunięciu prefiksu nic nie zostało.
        const cleaned = (trimmed.replace(streetPrefix, '').trim() || trimmed);
        const cleanedWords = cleaned.split(/\s+/);
        const cleanedMultiple = cleanedWords.length > 1;
        const cityQuery = cleanedMultiple ? cleanedWords[0] : cleaned;

        if (cityQuery.length >= 2) {
          const response = await shippingService.getPoints({
            city: cityQuery,
            per_page: 50,
            signal
          });
          if (response.success && response.data?.items) {
            allPoints = response.data.items;

            // Filtruj po pozostałych słowach (ulica/adres) - bez prefiksu
            if (cleanedMultiple && allPoints.length > 0) {
              const searchPart = cleanedWords.slice(1).join(' ').toLowerCase();
              allPoints = allPoints.filter(point => {
                const addr = (point.address?.line1 || '').toLowerCase();
                const name = (point.name || '').toLowerCase();
                return addr.includes(searchPart) || name.includes(searchPart);
              });
            }
          }
        }
      }

      // Jeśli brak wyników - spróbuj szukać po nazwie (bez prefiksu ulicy)
      if (allPoints.length === 0 && !isPostCode && trimmed.length >= 3) {
        const nameFallback = (trimmed.replace(streetPrefix, '').trim() || trimmed).toUpperCase();
        const nameResponse = await shippingService.getPoints({
          name: nameFallback.substring(0, 6),
          per_page: 30,
          signal
        });
        if (nameResponse.success && nameResponse.data?.items) {
          allPoints = nameResponse.data.items;
        }
      }

      // Ignoruj wynik jeśli w międzyczasie pojawiło się nowsze zapytanie
      if (!isCurrent()) return;
      setPoints(allPoints);
      setShowDropdown(true);
    } catch (err) {
      // Ciche pominięcie anulowanych/nieaktualnych zapytań
      if (signal.aborted || !isCurrent()) return;
      console.error('Błąd wyszukiwania paczkomatów:', err);
      setError('Błąd wyszukiwania paczkomatów');
      setPoints([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleSelectPoint = (point) => {
    onSelect(point);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleClearPoint = () => {
    onClear();
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const getPointTypeIcon = (type) => {
    switch (type) {
      case 'parcel_locker':
        return '📦';
      case 'pop':
        return '🏪';
      case 'parcel_locker_superpop':
        return '📦🏪';
      default:
        return '📍';
    }
  };

  const getPointTypeLabel = (type) => {
    switch (type) {
      case 'parcel_locker':
        return 'Paczkomat';
      case 'pop':
        return 'Punkt odbioru';
      case 'parcel_locker_superpop':
        return 'SuperPOP';
      default:
        return type;
    }
  };

  // Style
  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    backgroundColor: 'var(--card-bg)',
    color: 'var(--text)',
    outline: 'none'
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '0 0 4px 4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    maxHeight: '350px',
    overflowY: 'auto',
    zIndex: 1000
  };

  const itemStyle = {
    padding: '0.6rem 0.75rem',
    cursor: 'pointer',
    borderBottom: '1px solid var(--bg)',
    fontSize: '12px'
  };

  const buttonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem'
  };

  // Jeśli wybrany punkt - pokaż jego dane
  if (selectedPoint) {
    return (
      <div style={{
        backgroundColor: 'var(--warning-light)',
        border: '1px solid var(--warning)',
        borderRadius: '8px',
        padding: '0.75rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>
              {getPointTypeIcon(selectedPoint.type)} {selectedPoint.name}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {selectedPoint.address?.line1}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {selectedPoint.address?.line2}
            </div>
            <div style={{
              fontSize: '10px',
              color: 'var(--warning)',
              marginTop: '4px',
              display: 'inline-block',
              padding: '2px 6px',
              backgroundColor: 'var(--warning-light)',
              borderRadius: '3px'
            }}>
              {getPointTypeLabel(selectedPoint.type)}
            </div>
          </div>
          <button
            onClick={handleClearPoint}
            style={{ ...buttonStyle, border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
          >
            🔄 Zmień
          </button>
        </div>
      </div>
    );
  }

  // Wyszukiwarka punktów
  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '4px' }}>
        📦 {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => points.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          style={inputStyle}
        />
        {loading && (
          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
            ⏳
          </span>
        )}
      </div>

      {/* Dropdown z wynikami */}
      {showDropdown && points.length > 0 && (
        <div style={dropdownStyle}>
          {points.map((point) => (
            <div
              key={point.name}
              style={itemStyle}
              onClick={() => handleSelectPoint(point)}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--warning-light)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--card-bg)'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: '500' }}>
                    {getPointTypeIcon(point.type)} {point.name}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {point.address?.line1}, {point.address?.line2}
                  </div>
                </div>
                <span style={{
                  fontSize: '9px',
                  padding: '2px 6px',
                  backgroundColor: point.type === 'parcel_locker' ? 'var(--info-light)' : 'var(--bg)',
                  borderRadius: '3px',
                  color: point.type === 'parcel_locker' ? 'var(--info)' : 'var(--text-muted)'
                }}>
                  {getPointTypeLabel(point.type)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Brak wyników */}
      {showDropdown && searchTerm.length >= 2 && points.length === 0 && !loading && (
        <div style={{ ...dropdownStyle, padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div>Nie znaleziono paczkomatów</div>
          <div style={{ fontSize: '10px', marginTop: '0.5rem' }}>
            Wpisz nazwę miasta (np. Warszawa) lub kod pocztowy (00-000)
          </div>
          <div style={{ fontSize: '10px', marginTop: '0.25rem', color: 'var(--text-muted)' }}>
            Możesz też wpisać nazwę paczkomatu np. WAW123M
          </div>
        </div>
      )}

      {/* Błąd */}
      {error && (
        <div style={{ fontSize: '10px', color: 'var(--danger)', marginTop: '4px' }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default InPostPointSelector;
